import type { UseQueryResult } from "@tanstack/react-query";
import { useId } from "react";

import { useMe } from "@/api/hooks/auth";
import { useCourses } from "@/api/hooks/courses";
import type { CourseSummary } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { courseColor } from "@/lib/course-colors";
import { cn } from "@/lib/utils";

import { courseAction, coursePath } from "./nav-config";
import { SidebarActionLink, SidebarNavLink } from "./sidebar-link";

interface SidebarCoursesProps {
  onNavigate?: () => void;
}

/**
 * The viewer's active courses, plus the role-specific "New course" / "Join a course" action.
 * Each row leads with the course color and name, so names line up however long the codes
 * are; the code sits at the trailing edge and tells same-named courses apart.
 */
export function SidebarCourses({ onNavigate }: SidebarCoursesProps) {
  const me = useMe();
  const courses = useCourses();
  const headingId = useId();
  // On other tenants than the root site, only content admins create courses.
  const action = me && (me.role !== "instructor" || me.can_create_courses) ? courseAction(me.role) : null;

  return (
    <section aria-labelledby={headingId} className="space-y-1">
      <h2 id={headingId} className="px-2.5 pb-2 label-caps text-[11px] text-muted-foreground">
        Courses
      </h2>
      <CourseLinks query={courses} onNavigate={onNavigate} />
      {action && (
        <SidebarActionLink to={action.to} onNavigate={onNavigate}>
          <action.icon aria-hidden />
          {action.label}
        </SidebarActionLink>
      )}
    </section>
  );
}

interface CourseLinksProps {
  query: UseQueryResult<CourseSummary[]>;
  onNavigate?: () => void;
}

function CourseLinks({ query, onNavigate }: CourseLinksProps) {
  if (query.isPending) return <CourseLinksSkeleton />;
  if (query.isError) {
    return (
      <p className="px-2.5 py-1 text-xs text-muted-foreground">
        Couldn't load courses.{" "}
        <Button variant="link" className="h-auto p-0 text-xs" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </p>
    );
  }
  if (query.data.length === 0) return <p className="px-2.5 py-1 text-xs text-muted-foreground">No courses yet.</p>;

  return (
    <ul className="space-y-0.5">
      {query.data.map((course) => (
        <li key={course.id}>
          <SidebarNavLink to={coursePath(course.id)} onNavigate={onNavigate} title={`${course.code} · ${course.name}`}>
            <span aria-hidden className={cn("size-2 shrink-0 rounded-[3px]", courseColor(course.color).swatch)} />
            <span className="min-w-0 flex-1 truncate font-normal">{course.name}</span>
            <span className="max-w-[42%] shrink-0 truncate font-mono text-[10px] text-muted-foreground">
              {course.code}
            </span>
          </SidebarNavLink>
        </li>
      ))}
    </ul>
  );
}

function CourseLinksSkeleton() {
  return (
    <div role="status" aria-label="Loading courses" className="space-y-0.5">
      {["w-3/4", "w-5/6", "w-2/3"].map((width) => (
        <div key={width} className="flex h-8 items-center gap-2.5 px-2.5">
          <Skeleton className="size-2 rounded-[3px] bg-foreground/10" />
          <Skeleton className={cn("h-3 bg-foreground/10", width)} />
        </div>
      ))}
    </div>
  );
}
